import { teacherRequest } from "./teacherApi";

const apiBase = import.meta.env.VITE_API_BASE_URL ?? "";

export interface ArScanTarget {
  short_id: string;
  student_name: string;
  /** 🔑 같은 출처 경로다(서버가 중계한다). R2 서명 URL 이 아니다 — 아래 주석 참조. */
  marker_url: string;
  video_url: string;
}

export class ArScanNotFoundError extends Error {
  constructor() {
    super("키링을 찾을 수 없어요.");
    this.name = "ArScanNotFoundError";
  }
}

/**
 * 스캔 대상 해석. **로그인 없음** — 키링을 받은 아이도, 그 가족도 그냥 비추면 된다.
 *
 * 🚨 돌려받는 `marker_url`·`video_url` 은 **같은 출처 경로**다. R2 버킷에 CORS 가 없어서
 *    서명 URL 을 그대로 쓰면 MindAR 의 마커 요청도, 영상으로 만드는 WebGL 텍스처도
 *    교차 출처에서 막힌다(= 카메라만 켜지고 화면이 빈다). 절대 절대주소로 바꾸지 말 것.
 */
export async function fetchScanTarget(shortId: string, fetcher = fetch): Promise<ArScanTarget> {
  const response = await fetcher(`${apiBase}/api/ar/k/${encodeURIComponent(shortId)}`);
  if (response.status === 404) throw new ArScanNotFoundError();
  if (!response.ok) throw new Error("AR_SCAN_FAILED");
  return await response.json() as ArScanTarget;
}

export interface ArWorkRow {
  work_id: string;
  student_name: string;
  is_teacher_demo: boolean;
  has_final_video: boolean;
  short_id: string | null;
  version: number;
  published: boolean;
  revoked: boolean;
  moderation_status: string;
  /** 앞으로 몇 번 더 만들 수 있는가(018 `ar_per_student_limit`, 기본 3). */
  remaining_versions: number;
}

export function fetchArWorks(sessionId: string): Promise<{ works: ArWorkRow[] }> {
  return teacherRequest<{ works: ArWorkRow[] }>(
    `/api/ar/sessions/${encodeURIComponent(sessionId)}/works`,
  );
}

export interface ArFrameCandidate {
  frame_sec: number;
  /** 규칙 점수(0~1). 못 잰 지점은 null 이다 — 0 과 다르다. */
  score: number | null;
  recommended: boolean;
  /** data: URL. 후보는 R2 에 올리지 않는다 — 고르고 나면 나머지는 전부 쓰레기다. */
  preview: string;
}

export interface ArFramesResponse {
  duration_seconds: number;
  failed_seconds: number[];
  /**
   * 🔑 **추천이 없을 수 있다**(전부 못 쟀을 때 null). «없으면 첫 장» 으로 메우지 않는다 —
   *    「못 쟀다」와 「첫 장이 제일 좋다」는 다른 말이고, 그 차이는 인쇄물에 남는다.
   */
  recommended_frame_sec: number | null;
  candidates: ArFrameCandidate[];
}

export function fetchArFrames(workId: string): Promise<ArFramesResponse> {
  return teacherRequest<ArFramesResponse>(`/api/ar/works/${encodeURIComponent(workId)}/frames`);
}

export interface ArStagedKeyring {
  short_id: string;
  version: number;
  frame_sec: number;
  scan_url: string;
  thumbnail_url: string;
  qr_url: string;
  published: boolean;
}

export function stageArKeyring(
  workId: string,
  frameSec: number,
  markerScore?: number | null,
): Promise<ArStagedKeyring> {
  return teacherRequest<ArStagedKeyring>(`/api/ar/works/${encodeURIComponent(workId)}/keyring`, {
    method: "POST",
    body: JSON.stringify({
      frame_sec: frameSec,
      ...(typeof markerScore === "number" ? { marker_score: markerScore } : {}),
    }),
  });
}

/**
 * 키링 회수. 스캔이 즉시 «찾을 수 없어요» 가 된다.
 * 🚨 이미 인쇄되어 나간 종이는 되돌릴 수 없다 — 화면이 그 사실을 말해야 한다.
 */
export function revokeArKeyring(workId: string): Promise<{ revoked: boolean }> {
  return teacherRequest<{ revoked: boolean }>(
    `/api/ar/works/${encodeURIComponent(workId)}/revoke`,
    { method: "POST" },
  );
}

export interface ArPublishedKeyring {
  short_id: string;
  version: number;
  published: boolean;
  ar_expires_at: string;
}

/**
 * 마커를 붙여 발행한다.
 *
 * 🚨 **바이너리로 보낸다.** 실측 마커가 약 800KB 라 base64 로 싸면 1MB 가 넘고,
 *    서버의 `express.json({ limit: "256kb" })` 에 걸려 통째로 413 이 된다.
 */
export function uploadArMarker(workId: string, marker: Uint8Array): Promise<ArPublishedKeyring> {
  return teacherRequest<ArPublishedKeyring>(`/api/ar/works/${encodeURIComponent(workId)}/marker`, {
    method: "PUT",
    headers: { "content-type": "application/octet-stream" },
    body: new Blob([marker as BlobPart]),
  });
}
